import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import DashboardCustomizeOutlinedIcon from "@mui/icons-material/DashboardCustomizeOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import {
  resolveExerciseStateIdFromRow,
  TE_STATE_FINALIZED,
  TE_STATE_STARTED,
  TE_STATE_PAUSED,
  TE_STATE_CANCELLED,
} from "../../utils/coachExerciseNavigation";
import {
  sessiondeleteex,
  createSessionSubgroup,
  tablelist,
} from "../../components/funtionsBD/Mainservice";
import { SplitSubgroupDialog } from "../../components/planning/SplitSubgroupDialog";
import { DigipingMark } from "../../digiping";
import {
  hasMultipleLiveStations,
  stationLabel,
} from "../../utils/liveTrainingStations";
import { useLiveTrainingPortalRoot } from "../../hooks/useLiveFullscreen";
import { normalizeAppLang } from "../../utils/appLanguages";
import { isTypeOnlyBlock, plannedBlockTitle } from "../../utils/planningSubgroups";
import { isPersonalizedTrainingSlot } from "../../utils/personalizedTrainingSlot";
import {
  localizedExerciseName as catalogExerciseName,
  localizedTypeName,
} from "../../utils/localizedCatalogFields";
import "./LiveTrainingSessionPlanSheet.css";

function localizedExerciseName(ex, lng) {
  if (!ex) return "";
  const base = normalizeAppLang(lng);
  return (
    catalogExerciseName(ex, base) ||
    localizedTypeName(ex.type_ex, base) ||
    ""
  );
}

function blockStatusKey(stateId) {
  if (stateId === TE_STATE_FINALIZED) return "done";
  if (stateId === TE_STATE_STARTED) return "live";
  if (stateId === TE_STATE_PAUSED) return "paused";
  if (stateId === TE_STATE_CANCELLED) return "skipped";
  return "planned";
}

/**
 * Always-available coach view of the planned training blocks.
 * Add / change / remove reuse the Live Training exercise navigator.
 * Create stations (sub-groups) without leaving live training.
 */
export function LiveTrainingSessionPlanSheet({
  open,
  onClose,
  exercises = [],
  stations = [],
  focusedTsgId = null,
  currentTeId = null,
  realtimeByTeId = {},
  tsId = null,
  grId = null,
  onSelectStation = null,
  onSelectExercise = null,
  onAddExercise = null,
  onChangeExercise = null,
  onAddPlayer = null,
  onUseTemplate = null,
  onAiRecommend = null,
  onChanged = null,
  onStationCreated = null,
  onOpenPingflo = null,
  brandPrimary = "#0877BC",
  t: tProp,
}) {
  const { t: tHook, i18n } = useTranslation();
  const t = tProp || tHook;
  const lng = (i18n.language || "en").split("-")[0];
  const [busyTeId, setBusyTeId] = useState(null);
  const [error, setError] = useState("");
  const [splitOpen, setSplitOpen] = useState(false);
  const [splitBusy, setSplitBusy] = useState(false);
  const [roomTables, setRoomTables] = useState([]);

  const rows = useMemo(
    () => (Array.isArray(exercises) ? exercises.filter((ex) => ex?.te_id) : []),
    [exercises]
  );
  const portalRoot = useLiveTrainingPortalRoot();

  const stationCount = Array.isArray(stations) ? stations.length : 0;
  const showStations = hasMultipleLiveStations(stations);

  const openCreateStation = async () => {
    setError("");
    setSplitOpen(true);
    if (!tsId) {
      setRoomTables([]);
      return;
    }
    try {
      const tablesRes = await tablelist({
        gr_id: grId || undefined,
        ts_id: tsId,
        te_id: rows[0]?.te_id || currentTeId || undefined,
      });
      const tablesData = tablesRes?.data?.data || [];
      setRoomTables(Array.isArray(tablesData) ? tablesData : []);
    } catch {
      setRoomTables([]);
    }
  };

  const handleCreateStation = async ({ name, timer_mode, table_ids }) => {
    if (!tsId || splitBusy) return;
    setSplitBusy(true);
    setError("");
    try {
      const response = await createSessionSubgroup({
        ts_id: tsId,
        name,
        timer_mode,
        table_ids,
      });
      const created = response?.data?.data?.created || null;
      const nextSubgroups = response?.data?.data?.subgroups || [];
      setSplitOpen(false);
      onStationCreated?.({ created, subgroups: nextSubgroups });
      onChanged?.();
    } catch (err) {
      setError(
        err?.message ||
          t("errorCreatingSubGroup") ||
          "Could not create station"
      );
    } finally {
      setSplitBusy(false);
    }
  };

  if (!open) return null;

  const handleRemove = async (ex) => {
    if (!ex?.te_id || !tsId || busyTeId) return;
    const stateId = resolveExerciseStateIdFromRow(ex, realtimeByTeId);
    if (stateId === TE_STATE_STARTED || stateId === TE_STATE_PAUSED) {
      const ok =
        typeof window.confirm === "function"
          ? window.confirm(
              t(
                "liveTrainingPlanRemoveLiveConfirm",
                "This block is in progress. Remove it from the session plan?"
              )
            )
          : true;
      if (!ok) return;
    } else {
      const ok =
        typeof window.confirm === "function"
          ? window.confirm(
              t(
                "liveTrainingPlanRemoveConfirm",
                "Remove this training block from the session?"
              )
            )
          : true;
      if (!ok) return;
    }
    setBusyTeId(ex.te_id);
    setError("");
    try {
      await sessiondeleteex({ te_id: ex.te_id, ts_id: tsId });
      onChanged?.();
    } catch (err) {
      setError(err?.message || t("general") || "Could not remove exercise");
    } finally {
      setBusyTeId(null);
    }
  };

  if (!portalRoot) return null;

  const sheet = (
    <div
      className="lt-plan-sheet-backdrop"
      role="presentation"
      onClick={() => {
        if (splitOpen) return;
        onClose?.();
      }}
    >
      <div
        className="lt-plan-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="lt-plan-sheet-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="lt-plan-sheet__header">
          <div>
            <h2 id="lt-plan-sheet-title" className="lt-plan-sheet__title">
              {t("liveTrainingPlanTitle", "Session plan")}
            </h2>
            <p className="lt-plan-sheet__hint">
              {t(
                "liveTrainingPlanHint",
                "Ask Pingflo to design the session, or start from a template / AI plan."
              )}
            </p>
          </div>
          <div className="lt-plan-sheet__header-actions">
            {tsId && typeof onOpenPingflo === "function" ? (
              <button
                type="button"
                className="lt-plan-sheet__pingflo-btn"
                onClick={onOpenPingflo}
                aria-label={t(
                  "liveTrainingPlanPingflo",
                  "Ask Pingflo to design this session"
                )}
                title={t(
                  "liveTrainingPlanPingflo",
                  "Ask Pingflo to design this session"
                )}
                style={{ "--lt-pingflo-color": brandPrimary }}
              >
                <DigipingMark size={28} />
              </button>
            ) : null}
            <button
              type="button"
              className="lt-plan-sheet__icon-btn"
              onClick={onClose}
              aria-label={t("close") || "Close"}
            >
              <CloseIcon fontSize="small" />
            </button>
          </div>
        </header>

        {showStations ? (
          <div
            className="lt-plan-sheet__stations"
            role="tablist"
            aria-label={t("liveTrainingStations", "Stations")}
          >
            {stations.map((station) => {
              const active =
                String(station.tsg_id) === String(focusedTsgId || "");
              return (
                <button
                  key={String(station.tsg_id)}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  className={`lt-plan-sheet__station-chip${
                    active ? " lt-plan-sheet__station-chip--active" : ""
                  }`}
                  style={
                    station.color
                      ? { "--lt-station-accent": station.color }
                      : undefined
                  }
                  onClick={() => onSelectStation?.(station.tsg_id)}
                >
                  {stationLabel(station, t)}
                </button>
              );
            })}
            {typeof onAddPlayer === "function" ? (
              <button
                type="button"
                className="lt-plan-sheet__station-add-player"
                onClick={() => onAddPlayer?.()}
                title={t("addPlayer") || "Add player"}
                aria-label={t("addPlayer") || "Add player"}
              >
                <PersonAddAlt1Icon fontSize="small" />
                {t("addPlayer") || "Add player"}
              </button>
            ) : null}
          </div>
        ) : typeof onAddPlayer === "function" ? (
          <div className="lt-plan-sheet__stations">
            <button
              type="button"
              className="lt-plan-sheet__station-add-player"
              onClick={() => onAddPlayer?.()}
              title={t("addPlayer") || "Add player"}
              aria-label={t("addPlayer") || "Add player"}
            >
              <PersonAddAlt1Icon fontSize="small" />
              {t("addPlayer") || "Add player"}
            </button>
          </div>
        ) : null}

        <div className="lt-plan-sheet__list" role="list">
          {rows.length === 0 ? (
            <p className="lt-plan-sheet__empty">
              {t(
                "liveTrainingPlanEmpty",
                "No exercises yet. Add the first block to start."
              )}
            </p>
          ) : (
            rows.map((ex, index) => {
              const stateId = resolveExerciseStateIdFromRow(ex, realtimeByTeId);
              const status = blockStatusKey(stateId);
              const personalized = isPersonalizedTrainingSlot(ex);
              const name = personalized
                ? t("personalizedExercise", "Personalized exercise")
                : plannedBlockTitle(ex, lng) ||
                  localizedExerciseName(ex, lng) ||
                  t("exercise") ||
                  `Block ${index + 1}`;
              const duration = parseInt(ex.te_duration, 10) || null;
              const isCurrent = String(ex.te_id) === String(currentTeId || "");
              const busy = String(busyTeId) === String(ex.te_id);
              return (
                <div
                  key={ex.te_id}
                  className={`lt-plan-sheet__row${
                    isCurrent ? " lt-plan-sheet__row--current" : ""
                  }${personalized ? " lt-plan-sheet__row--personalized" : ""}`}
                  role="listitem"
                >
                  <button
                    type="button"
                    className="lt-plan-sheet__row-main"
                    onClick={() => {
                      onSelectExercise?.(ex);
                      onClose?.();
                    }}
                  >
                    <span className="lt-plan-sheet__index">{index + 1}</span>
                    <span className="lt-plan-sheet__copy">
                      <span className="lt-plan-sheet__name">{name}</span>
                      <span className="lt-plan-sheet__meta">
                        <span
                          className={`lt-plan-sheet__status lt-plan-sheet__status--${status}`}
                        >
                          {t(`liveTrainingPlanStatus_${status}`, status)}
                        </span>
                        {duration ? (
                          <span>
                            {duration} {t("min") || "min"}
                          </span>
                        ) : null}
                        {isTypeOnlyBlock(ex) ? (
                          <span>{t("assignExerciseLater") || "Assign later"}</span>
                        ) : null}
                      </span>
                    </span>
                  </button>
                  <div className="lt-plan-sheet__actions">
                    <button
                      type="button"
                      className="lt-plan-sheet__action"
                      disabled={busy || status === "done"}
                      onClick={() => onChangeExercise?.(ex)}
                      aria-label={t("liveTrainingPlanChange", "Change")}
                      title={t("liveTrainingPlanChange", "Change")}
                    >
                      <SwapHorizIcon fontSize="small" />
                    </button>
                    <button
                      type="button"
                      className="lt-plan-sheet__action lt-plan-sheet__action--danger"
                      disabled={busy || status === "done"}
                      onClick={() => handleRemove(ex)}
                      aria-label={t("liveTrainingPlanRemove", "Remove")}
                      title={t("liveTrainingPlanRemove", "Remove")}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {error ? (
          <p className="lt-plan-sheet__error" role="alert">
            {error}
          </p>
        ) : null}

        <footer className="lt-plan-sheet__footer">
          {typeof onUseTemplate === "function" ? (
            <button
              type="button"
              className="lt-plan-sheet__template"
              data-tour-id="live-training-use-template"
              onClick={() => onUseTemplate()}
            >
              <DashboardCustomizeOutlinedIcon fontSize="small" />
              {t("useTemplateShort", "Template")}
            </button>
          ) : null}
          {typeof onAiRecommend === "function" ? (
            <button
              type="button"
              className="lt-plan-sheet__ai"
              data-tour-id="live-training-ai-plan"
              onClick={() => onAiRecommend()}
            >
              <AutoAwesomeIcon fontSize="small" />
              {t("liveTrainingPlanAi", "AI plan")}
            </button>
          ) : null}
          <button
            type="button"
            className="lt-plan-sheet__add"
            data-tour-id="live-training-open"
            onClick={() => {
              onAddExercise?.();
            }}
          >
            <AddIcon fontSize="small" />
            {t("liveTrainingAddExercise", "Add exercise")}
          </button>
          <button
            type="button"
            className="lt-plan-sheet__station"
            data-tour-id="live-training-create-station"
            disabled={!tsId || splitBusy}
            onClick={openCreateStation}
          >
            <AccountTreeIcon fontSize="small" />
            {t("liveTrainingCreateStation", "New station")}
          </button>
        </footer>
      </div>

      <SplitSubgroupDialog
        open={splitOpen}
        onClose={() => !splitBusy && setSplitOpen(false)}
        onCreate={handleCreateStation}
        tables={roomTables}
        existingCount={Math.max(1, stationCount)}
        busy={splitBusy}
        zIndex={4600}
        container={portalRoot}
      />
    </div>
  );

  return createPortal(sheet, portalRoot);
}

export default LiveTrainingSessionPlanSheet;
